import { beginFeedback } from '../../lib/interactionFeedback';
// src/components/evidence/EvidenceDrawer.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "components/ui/sheet";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Badge } from "components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  Edit3,
  Save,
  X,
  Trash2,
  Link2,
  Calendar,
  Plus,
  Search as SearchIcon,
  Loader2,
} from "lucide-react";
import EvidenceAssessmentFields, { EVIDENCE_RELATIONSHIPS } from './EvidenceAssessmentFields';
import { evidenceRelationship, scoreEvidenceForProblem } from '../../lib/validationScore';
import EvidencePreviewCard from "./EvidencePreviewCard";
import { fetchOgMeta } from "lib/ogClient";
import { evidenceService } from "../../services/database";

const EVIDENCE_TYPES = [
  "interview",
  "survey",
  "usability",
  "support_ticket",
  "heatmap",
  "tech_spike",
  "architecture",
  "feasibility_doc",
  "repo_link",
  "ab_test",
  "analytics",
  "cohort",
  "pricing",
  "finance",
  "link",
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(`(max-width:${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    try {
      mql.addEventListener("change", onChange);
    } catch {
      mql.addListener(onChange);
    }
    setIsMobile(mql.matches);
    return () => {
      try {
        mql.removeEventListener("change", onChange);
      } catch {
        mql.removeListener(onChange);
      }
    };
  }, [breakpoint]);
  return isMobile;
}

// Updated to use Supabase directly
// function getEntitiesSafe() {
//   // No longer needed with Supabase services
//   return { Evidence: evidenceService };
// }
function resolveProblemId(item, fallback) {
  return (
    item?.problem_card_id ??
    item?.problem_id ??
    item?.problemId ??
    item?.problem ??
    fallback
  );
}
async function findEvidenceIdBySignature(target) {
  const list = await evidenceService.list("-updated_at", 9999);
  const pid = resolveProblemId(target);
  const url = (target?.url || "").trim();
  const match = list.find(
    (e) =>
      String(resolveProblemId(e)) === String(pid) &&
      (e?.url || "").trim() === url
  );
  return match?.id;
}
async function robustUpdateEvidence(idOrItem, patch) {
  let id =
    typeof idOrItem === "string" || typeof idOrItem === "number"
      ? idOrItem
      : idOrItem?.id;

  if (!id) id = await findEvidenceIdBySignature(idOrItem);
  if (!id) throw new Error("Evidence id not found for update");

  try {
    return await evidenceService.update(id, patch);
  } catch (error) {
    console.error('Error updating evidence:', error);
    throw error;
  }
}
async function robustDeleteEvidence(itemOrId) {
  let id =
    typeof itemOrId === "string" || typeof itemOrId === "number"
      ? itemOrId
      : itemOrId?.id;
  if (!id) id = await findEvidenceIdBySignature(itemOrId);
  if (!id) return;

  try {
    return await evidenceService.delete(id);
  } catch (error) {
    console.error('Error deleting evidence:', error);
    throw error;
  }
}

export default function EvidenceDrawer({
  open,
  onClose,
  problem,
  evidence = [],
  onChanged,
  pageSizeDefault = 10,
}) {
  const isMobile = useIsMobile(768);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeDefault);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [assessment, setAssessment] = useState({ relationship: 'unreviewed', source_identity: '' });
  const summary = scoreEvidenceForProblem(evidence, problem?.id);

  // OG preview state (quick add + edit)
  const [ogLoading, setOgLoading] = useState(false);
  const [ogMeta, setOgMeta] = useState(null);

  // Quick add form
  const [addType, setAddType] = useState("interview");
  const [addUrl, setAddUrl] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addDate, setAddDate] = useState("");
  const [addStrength, setAddStrength] = useState("3");

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setPage(1);
    setPageSize(isMobile ? Math.min(10, pageSizeDefault) : pageSizeDefault);
    setEditingId(null);
    setEditingDraft({});
    setSubmitting(false); setError(''); setAssessment({ relationship: 'unreviewed', source_identity: '' });

    setOgLoading(false);
    setOgMeta(null);
    setAddType("interview");
    setAddUrl("");
    setAddNote("");
    setAddDate("");
    setAddStrength("3");
  }, [open, problem, pageSizeDefault, isMobile]);

  // rows for this problem + search
  const rows = useMemo(() => {
    const pid = problem?.id;
    const list = evidence.filter(
      (e) => String(resolveProblemId(e)) === String(pid)
    );
    if (!(query || "").trim()) return list;
    const q = query.trim().toLowerCase();
    return list.filter((e) => {
      const txt =
        [
          e?.type,
          e?.url,
          e?.note || e?.notes,
          e?.dateISO,
          String(e?.strength ?? ""),
          e?.og_title,
          e?.og_description,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase() || "";
      return txt.includes(q);
    });
  }, [evidence, problem, query]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const clampedPage = Math.min(page, pageCount);
  const pageStart = (clampedPage - 1) * pageSize;
  const pageRows = rows.slice(pageStart, pageStart + pageSize);
  const begin = rows.length === 0 ? 0 : pageStart + 1;
  const end = Math.min(rows.length, pageStart + pageSize);

  const startEdit = (row) => {
    setEditingId(row?.id || `sig:${problem?.id}::${row?.url || ""}`);
    setEditingDraft({
      type: row?.type || "link",
      url: row?.url || "",
      note: row?.note || row?.notes || "",
      dateISO: row?.dateISO || "",
      strength: String(row?.strength ?? "3"),
      relationship: evidenceRelationship(row), source_identity: row?.source_identity || '',
      // Existing OG fields are preserved for inline preview while editing
      og_title: row?.og_title || "",
      og_description: row?.og_description || "",
      og_image: row?.og_image || "",
      og_site: row?.og_site || "",
      og_favicon: row?.og_favicon || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft({});
    setOgMeta(null);
    setOgLoading(false);
  };

  // Shared OG fetcher (used by quick add + edit)
  const fetchOG = useCallback(
    async (rawUrl) => {
      const u = (rawUrl || "").trim();
      try {
        new URL(u);
      } catch {
        setOgMeta(null);
        return null;
      }
      setOgLoading(true);
      try {
        const data = await fetchOgMeta(u);
        setOgMeta(data);
        return data;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("OG fetch error:", e);
        setOgMeta(null);
        return null;
      } finally {
        setOgLoading(false);
      }
    },
    [setOgMeta, setOgLoading]
  );

  const saveEdit = async (row) => {
    setSubmitting(true); setError('');
    try {
      const pid = resolveProblemId(row, problem?.id);
      await robustUpdateEvidence(row?.id || row, {
        problem_id: pid,
        type: editingDraft.type,
        url: editingDraft.url,
        note: editingDraft.note,
        dateISO: editingDraft.dateISO,
        strength: Number(editingDraft.strength || 3),
        relationship: editingDraft.relationship, source_identity: editingDraft.source_identity.trim(),
        // Persist OG fields if present (existing or from last fetch)
        og_title: editingDraft.og_title || ogMeta?.title || "",
        og_description: editingDraft.og_description || ogMeta?.description || "",
        og_image: editingDraft.og_image || ogMeta?.image || "",
        og_site: editingDraft.og_site || ogMeta?.siteName || "",
        og_favicon: editingDraft.og_favicon || ogMeta?.favicon || "",
      });
      await onChanged?.();
      cancelEdit();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Save evidence failed:", err); setError(err.message || 'Could not save evidence.');
    }
    setSubmitting(false);
  };

  const deleteRow = async (row) => {
    setSubmitting(true); setError('');
    try {
      await robustDeleteEvidence(row);
      await onChanged?.();
      if (begin === end && clampedPage > 1) setPage((p) => p - 1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Delete evidence failed:", err); setError(err.message || 'Could not delete evidence.');
    }
    setSubmitting(false);
  };

  const addEvidence = async (e) => {
    e.preventDefault();
    const feedback = beginFeedback(e, 'evidence');
    if (!problem?.id) return;
    if (!addUrl.trim() && !addNote.trim()) { setError('Add a source URL or describe the observation.'); return; }
    setSubmitting(true); setError('');
    try {
      await evidenceService.create({
        problem_id: problem.id,
        type: addType,
        ...assessment,
        url: addUrl.trim(),
        note: addNote.trim(),
        dateISO: addDate,
        strength: Number(addStrength || 3),
        // Persist OG fields (if a preview was done)
        og_title: ogMeta?.title || "",
        og_description: ogMeta?.description || "",
        og_image: ogMeta?.image || "",
        og_site: ogMeta?.siteName || "",
        og_favicon: ogMeta?.favicon || "",
      });

      await feedback.complete('Evidence captured', problem?.title);
      await onChanged?.();

      setAssessment({ relationship: 'unreviewed', source_identity: '' });
      // reset quick add
      setAddType("interview");
      setAddUrl("");
      setAddNote("");
      setAddDate("");
      setAddStrength("3");
      setOgMeta(null);
      setOgLoading(false);
      setPage(1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Add evidence failed:", err); setError(err.message || 'Could not add evidence.');
    }
    setSubmitting(false);
  };

  return (
    <Sheet open={open} onOpenChange={(val) => !val && onClose?.()}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        style={{
          width: isMobile ? "100vw" : "min(920px, 96vw)",
          maxWidth: isMobile ? "100vw" : "96vw",
          height: isMobile ? "100dvh" : "100vh",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? 12 : 16,
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-inset)",
          }}
        >
          <SheetHeader>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <div>
                <SheetTitle style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.2 }}>
                  Evidence for{" "}
                  <span style={{ color: "var(--fg)", fontWeight: 800 }}>
                    {problem?.title || `#${problem?.id}`}
                  </span>
                </SheetTitle>
                <SheetDescription style={{ display: "none" }}>
                  Manage, search, edit and add evidence items
                </SheetDescription>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  className="text-xs"
                  style={{
                    background: "var(--border)",
                    color: "var(--fg)",
                    border: 0,
                    height: 24,
                  }}
                >
                  {rows.length} items
                </Badge>
                <SheetClose asChild>
                  <Button variant="outline" size="icon" title="Close">
                    <X size={16} />
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetHeader>
        </div>

        {/* Toolbar */}
        <div
          style={{
            padding: isMobile ? 10 : 12,
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: isMobile ? 0 : 220 }}>
              <Input
                placeholder="Search evidence (type, url, note, strength, date, title)…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                style={{ paddingLeft: 32, height: isMobile ? 38 : undefined }}
              />
              <SearchIcon
                size={16}
                color="var(--fg-subtle)"
                style={{ position: "absolute", left: 10, top: isMobile ? 11 : 9 }}
              />
            </div>

            {!isMobile && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  style={{
                    height: 34,
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    padding: "0 8px",
                    background: "var(--bg-surface)",
                  }}
                >
                  {[5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {error && <p role="alert" className="v2-error">{error}</p>}
        <p className="evidence-summary">{summary.supportingCount} supporting · {summary.contradictingCount} contradictory · {summary.unreviewedCount} unreviewed source groups. Repeated source IDs count once per assessment. Coverage is not confidence.</p>
        {/* Quick Add */}
        <form
          onSubmit={addEvidence}
          style={{
            padding: isMobile ? 10 : 12,
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "1fr 3fr 2fr 1fr 1fr auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            {/* Type */}
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={addType} onValueChange={setAddType}>
                <SelectTrigger style={{ marginTop: 6, height: isMobile ? 38 : undefined }}>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {EVIDENCE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* URL (optional)/}
            <div>
              <Label className="text-xs">URL (optional)</Label>
              <div className="flex gap-2" style={{ marginTop: 6 }}>
                <Input
                  placeholder="https://..."
                  value={addUrl}
                  onChange={(e) => setAddUrl(e.target.value)}
                  onBlur={() => fetchOG(addUrl)}
                  style={{ height: isMobile ? 38 : undefined }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchOG(addUrl)}
                  disabled={!addUrl || ogLoading}
                >
                  {ogLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Preview
                </Button>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label className="text-xs">Note</Label>
              <Input
                placeholder="short context"
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                style={{ marginTop: 6, height: isMobile ? 38 : undefined }}
              />
            </div>

            {/* Date */}
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
                style={{ marginTop: 6, height: isMobile ? 38 : undefined }}
              />
            </div>

            {/* Strength */}
            <div>
              <Label className="text-xs">Strength</Label>
              <Select value={addStrength} onValueChange={setAddStrength}>
                <SelectTrigger style={{ marginTop: 6, height: isMobile ? 38 : undefined }}>
                  <SelectValue placeholder="1..5" />
                </SelectTrigger>
                <SelectContent>
                  {["1", "2", "3", "4", "5"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add button */}
            <div style={{ alignSelf: isMobile ? "stretch" : "end" }}>
              <Button
                type="submit"
                disabled={submitting}
                style={{
                  background: "var(--accent-surface)",
                  border: "none",
                  height: isMobile ? 40 : undefined,
                }}
              >
                <Plus size={16} style={{ marginRight: 6 }} /> Add
              </Button>
            </div>
          </div>

          <EvidenceAssessmentFields value={assessment} onChange={setAssessment} prefix="New evidence"/>
          {/* OG preview under quick add row */}
          {ogMeta ? (
            <div className="mt-3">
              <EvidencePreviewCard data={ogMeta} compact />
            </div>
          ) : null}
        </form>

        {/* List / Table */}
        <div style={{ flex: 1, overflowY: "auto", background: "var(--bg-inset)" }}>
          {rows.length === 0 ? (
            <div
              style={{
                padding: isMobile ? 16 : 24,
                textAlign: "center",
                color: "var(--fg-muted)",
              }}
            >
              No evidence yet. Add your first item above.
            </div>
          ) : (
            <EvidenceList
              isMobile={isMobile}
              rows={pageRows}
              problem={problem}
              editingId={editingId}
              editingDraft={editingDraft}
              setEditingDraft={setEditingDraft}
              startEdit={startEdit}
              cancelEdit={cancelEdit}
              saveEdit={saveEdit}
              deleteRow={deleteRow}
              fetchOG={fetchOG}
              ogLoading={ogLoading}
            />
          )}
        </div>

        {/* Footer / Pagination */}
        <div
          style={{
            padding: isMobile ? 10 : 12,
            borderTop: "1px solid var(--border)",
            background: "var(--bg-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "center" : "space-between",
            gap: 12,
          }}
        >
          {!isMobile && (
            <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
              {begin}-{end} of {rows.length}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, width: isMobile ? "100%" : "auto" }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={clampedPage <= 1}
              style={{ flex: isMobile ? 1 : undefined, height: isMobile ? 40 : undefined }}
            >
              <ChevronLeft size={16} /> Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={clampedPage >= pageCount}
              style={{ flex: isMobile ? 1 : undefined, height: isMobile ? 40 : undefined }}
            >
              Next <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ---- Row list (desktop grid + mobile card) ---- */
function EvidenceList({
  isMobile,
  rows,
  problem,
  editingId,
  editingDraft,
  setEditingDraft,
  startEdit,
  cancelEdit,
  saveEdit,
  deleteRow,
  fetchOG,
  ogLoading,
}) {
  const hostFromUrl = (u) => {
    try {
      return new URL(u).host.replace(/^www\./, "");
    } catch {
      return "";
    }
  };

  if (!isMobile) {
    return (
      <div style={{ padding: 12, display: "grid", gap: 8 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 3fr 2fr 1fr 0.8fr 160px",
            gap: 8,
            fontSize: 12,
            color: "var(--fg-muted)",
            padding: "6px 8px",
          }}
        >
          <div>Type</div>
          <div>URL</div>
          <div>Note</div>
          <div>Date</div>
          <div>Strength</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>

        {rows.map((row) => {
          const idKey = row?.id || `sig:${problem?.id}::${row?.url || ""}`;
          const isEditing = editingId === idKey;
          const host = hostFromUrl(row?.url || "");

          return (
            <div
              key={idKey}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 3fr 2fr 1fr 0.8fr 160px",
                gap: 8,
                padding: "8px",
                border: "1px solid var(--border)",
                borderRadius: 10,
                background: "var(--bg-surface)",
                alignItems: "center",
              }}
            >
              <div style={{ gridColumn: '1 / -1' }}>{isEditing ? <EvidenceAssessmentFields value={editingDraft} onChange={setEditingDraft} prefix="Edit evidence"/> : <small>{EVIDENCE_RELATIONSHIPS[evidenceRelationship(row)]} · {row.source_identity || 'Source identity not recorded'}</small>}</div>
              {/* type */}
              <div>
                {isEditing ? (
                  <Select
                    value={editingDraft.type}
                    onValueChange={(v) =>
                      setEditingDraft((d) => ({ ...d, type: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVIDENCE_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    className="text-xs"
                    style={{
                      background: "var(--accent-soft)",
                      color: "var(--accent-fg)",
                      border: 0,
                    }}
                  >
                    {row?.type || "link"}
                  </Badge>
                )}
              </div>

              {/* url */}
              <div style={{ minWidth: 0 }}>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editingDraft.url}
                      onChange={(e) =>
                        setEditingDraft((d) => ({ ...d, url: e.target.value }))
                      }
                      onBlur={() => fetchOG(editingDraft.url)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fetchOG(editingDraft.url)}
                      disabled={!editingDraft.url || ogLoading}
                    >
                      {ogLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Preview
                    </Button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      minWidth: 0,
                    }}
                  >
                    <Link2 size={14} color="var(--fg-muted)" />
                    <a
                      href={row?.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={row?.url}
                      style={{
                        color: "var(--accent-fg)",
                        textDecoration: "none",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {row?.url}
                    </a>
                    {host && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "var(--fg-subtle)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        • {host}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* note */}
              <div>
                {isEditing ? (
                  <Input
                    value={editingDraft.note}
                    onChange={(e) =>
                      setEditingDraft((d) => ({ ...d, note: e.target.value }))
                    }
                  />
                ) : (
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row?.note || row?.notes || ""}
                  >
                    {row?.note || row?.notes || ""}
                  </span>
                )}
              </div>

              {/* date */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Calendar size={14} color="var(--fg-muted)" />
                {isEditing ? (
                  <Input
                    type="date"
                    value={editingDraft.dateISO}
                    onChange={(e) =>
                      setEditingDraft((d) => ({ ...d, dateISO: e.target.value }))
                    }
                    style={{ flex: 1 }}
                  />
                ) : (
                  <span style={{ fontSize: 12, color: "var(--fg-secondary)" }}>
                    {row?.dateISO || "—"}
                  </span>
                )}
              </div>

              {/* strength */}
              <div>
                {isEditing ? (
                  <Select
                    value={editingDraft.strength}
                    onValueChange={(v) =>
                      setEditingDraft((d) => ({ ...d, strength: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="1..5" />
                    </SelectTrigger>
                    <SelectContent>
                      {["1", "2", "3", "4", "5"].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    className="text-xs"
                    style={{ background: "var(--bg-subtle)", color: "var(--fg)", border: 0 }}
                  >
                    S{Number(row?.strength ?? 3)}
                  </Badge>
                )}
              </div>

              {/* actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => saveEdit(row)}
                      style={{ background: "var(--success)", border: "none" }}
                    >
                      <Save size={14} style={{ marginRight: 6 }} />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>
                      <X size={14} style={{ marginRight: 6 }} />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                      <Edit3 size={14} style={{ marginRight: 6 }} />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteRow(row)}
                    >
                      <Trash2 size={14} style={{ marginRight: 6 }} />
                      Delete
                    </Button>
                  </>
                )}
              </div>

              {/* inline OG preview while editing */}
              {isEditing &&
              (editingDraft.og_title ||
                editingDraft.og_description ||
                editingDraft.og_image) ? (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div className="mt-3">
                    <EvidencePreviewCard
                      data={{
                        url: editingDraft.url,
                        title: editingDraft.og_title,
                        description: editingDraft.og_description,
                        image: editingDraft.og_image,
                        siteName: editingDraft.og_site,
                        favicon: editingDraft.og_favicon,
                      }}
                      compact
                    />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  // mobile card list
  return (
    <div style={{ padding: 10, display: "grid", gap: 8 }}>
      {rows.map((row) => {
        const idKey = row?.id || `sig:${problem?.id}::${row?.url || ""}`;
        const isEditing = editingId === idKey;

        return (
          <div
            key={idKey}
            style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--bg-surface)" }}
          >
            <div style={{ padding: 10, display: "grid", gap: 8 }}>
              {isEditing ? <EvidenceAssessmentFields value={editingDraft} onChange={setEditingDraft} prefix="Edit evidence"/> : <small>{EVIDENCE_RELATIONSHIPS[evidenceRelationship(row)]} · {row.source_identity || 'Source identity not recorded'}</small>}
              {/* type + strength */}
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Label className="text-xs">Type</Label>
                  {isEditing ? (
                    <Select
                      value={editingDraft.type}
                      onValueChange={(v) =>
                        setEditingDraft((d) => ({ ...d, type: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVIDENCE_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge
                      className="text-xs"
                      style={{
                        background: "var(--accent-soft)",
                        color: "var(--accent-fg)",
                        border: 0,
                      }}
                    >
                      {row?.type || "link"}
                    </Badge>
                  )}
                </div>
                <div style={{ width: 100 }}>
                  <Label className="text-xs">Strength</Label>
                  {isEditing ? (
                    <Select
                      value={editingDraft.strength}
                      onValueChange={(v) =>
                        setEditingDraft((d) => ({ ...d, strength: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="1..5" />
                      </SelectTrigger>
                      <SelectContent>
                        {["1", "2", "3", "4", "5"].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge
                      className="text-xs"
                      style={{ background: "var(--bg-subtle)", color: "var(--fg)", border: 0 }}
                    >
                      S{Number(row?.strength ?? 3)}
                    </Badge>
                  )}
                </div>
              </div>

              {/* url + preview */}
              <div>
                <Label className="text-xs">URL</Label>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Input
                      value={editingDraft.url}
                      onChange={(e) =>
                        setEditingDraft((d) => ({ ...d, url: e.target.value }))
                      }
                      onBlur={() => fetchOG(editingDraft.url)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fetchOG(editingDraft.url)}
                      disabled={!editingDraft.url || ogLoading}
                    >
                      {ogLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Preview
                    </Button>
                  </div>
                ) : (
                  <a
                    href={row?.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--accent-fg)" }}
                  >
                    {row?.url}
                  </a>
                )}
              </div>

              {/* inline preview (edit mode) */}
              {isEditing &&
              (editingDraft.og_title ||
                editingDraft.og_description ||
                editingDraft.og_image) ? (
                <EvidencePreviewCard
                  data={{
                    url: editingDraft.url,
                    title: editingDraft.og_title,
                    description: editingDraft.og_description,
                    image: editingDraft.og_image,
                    siteName: editingDraft.og_site,
                    favicon: editingDraft.og_favicon,
                  }}
                  compact
                />
              ) : null}

              {/* note + date */}
              <div>
                <Label className="text-xs">Note</Label>
                {isEditing ? (
                  <Input
                    value={editingDraft.note}
                    onChange={(e) =>
                      setEditingDraft((d) => ({ ...d, note: e.target.value }))
                    }
                  />
                ) : (
                  <div>{row?.note || row?.notes || "—"}</div>
                )}
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editingDraft.dateISO}
                    onChange={(e) =>
                      setEditingDraft((d) => ({ ...d, dateISO: e.target.value }))
                    }
                  />
                ) : (
                  <div>{row?.dateISO || "—"}</div>
                )}
              </div>

              {/* actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => saveEdit(row)}
                      style={{ background: "var(--success)", border: "none" }}
                    >
                      <Save size={14} style={{ marginRight: 6 }} />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>
                      <X size={14} style={{ marginRight: 6 }} />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                      <Edit3 size={14} style={{ marginRight: 6 }} />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteRow(row)}
                    >
                      <Trash2 size={14} style={{ marginRight: 6 }} />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
